import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';

import { ApiService } from '@/features/api';
import { useSyncKoObservable } from '@/hooks/useSyncKoObservable';
import { Instance as CompanyService } from '@/legacy/services/company';
import CompanyContext from '@/providers/CompanyContext';

enum VerificationStatus {
  Pending = 'PENDING',
  Processing = 'PROCESSING',
  Completed = 'COMPLETED',
  Rejected = 'REJECTED',
}

interface OwnershipVerificationModel {
  id: string;
  verificationStatus: VerificationStatus;
  notificationFirstSentDate: string | null;
  nonComplianceDateDeadline: string | null;
  personaInquiryId: string | null;
}

export const useOwnershipVerification = () => {
  const { companyId } = useContext(CompanyContext);

  const profile = useSyncKoObservable(CompanyService.profile);
  const isSubjectToPersonaOnboarding = useSyncKoObservable(profile?.isSubjectToPersonaOnboarding);

  const {
    data: ownershipVerification,
    isInitialLoading,
    isError,
  } = useQuery({
    queryFn: () =>
      ApiService.get<OwnershipVerificationModel>(
        `${import.meta.env.FEATURE_VERIFICATION_BASE_URL}/verifications/company/${companyId}/ownership`
      ),
    queryKey: ['verifications', 'company', companyId, 'ownership'],
    // companies which are subject to Persona onboarding will never go through
    // the ownership verification flow, so don't call the ownership endpoint.
    enabled: isSubjectToPersonaOnboarding === false,
  });

  const isOwnershipVerificationRequired =
    !isInitialLoading && !isError
      ? Boolean(ownershipVerification?.notificationFirstSentDate) &&
        ownershipVerification?.verificationStatus === VerificationStatus.Pending
      : null;

  return {
    isInitialLoading,
    isOwnershipVerificationRequired,
    personaInquiryId: ownershipVerification?.personaInquiryId,
  };
};
